/**
 * The three standing status probes, as shared queries (UI overhaul PR-2).
 *
 * Before: `SystemHealthControl` ran its own `/api/health`, `/api/freshness`
 * and `/api/ppc-freshness` intervals, and `BackendStatus`, `FreshnessBadge`
 * and `PpcFreshnessChip` each ran the same ones again while the dropdown was
 * open — none gated on tab visibility (the two standing GETs the hidden-tab
 * census counted). Now each probe is ONE query; every reader shares the
 * answer, one timer runs per probe, and a hidden browser tab runs none.
 *
 * Consecutive misses are counted per probe at the query cache
 * (`failures.ts`) so the header can escalate a probe that has been
 * unreachable for a while instead of sitting on "Checking systems…" forever.
 * The probes retry 0 times: a poll that fails is a miss, not a reason to
 * hammer a backend that is already struggling.
 */
import { api, apiObs } from '../api'
import { keys } from './keys'
import { useApiQuery } from './useApiQuery'

export type HealthOut = Awaited<ReturnType<typeof apiObs.getHealth>>
export type FreshnessOverview = Awaited<ReturnType<typeof api.getFreshness>>
export type PpcFreshness = Awaited<ReturnType<typeof api.getPpcFreshness>>

export const HEALTH_POLL_MS = 15_000
/** How often a control that is waiting for the backend to come back (the
 *  dev Start/Restart buttons) asks. */
export const HEALTH_FAST_POLL_MS = 1_000
/** A liveness probe that has not answered in this long is a miss — one
 *  cadence at the normal rate (a slow backend is not a dead one), tighter
 *  while a Start/Restart is being watched. */
export const HEALTH_TIMEOUT_MS = 15_000
export const HEALTH_FAST_TIMEOUT_MS = 5_000
export const FRESHNESS_POLL_MS = 60_000
/** While a PPC sync is in flight the chip follows it closely. */
export const PPC_SYNCING_POLL_MS = 4_000

const fetchHealth = () => apiObs.getHealth()
const fetchFreshness = () => api.getFreshness()
const fetchPpcFreshness = () => api.getPpcFreshness()

const ppcCadence = (d: PpcFreshness | undefined): number =>
  d?.is_refreshing ? PPC_SYNCING_POLL_MS : FRESHNESS_POLL_MS

/** GET /api/health every 15 s while visible. `isError` = the backend is not
 *  answering; `data` = it is. `fast` asks for the 1 s cadence — the shared
 *  timer runs at the fastest cadence any reader wants (lib/query/polling.ts). */
export function useBackendHealth(opts: { fast?: boolean } = {}) {
  return useApiQuery<HealthOut>(keys.health(), fetchHealth, {
    poll: opts.fast ? HEALTH_FAST_POLL_MS : HEALTH_POLL_MS,
    staleTime: 10_000, retry: 0,
    timeoutMs: opts.fast ? HEALTH_FAST_TIMEOUT_MS : HEALTH_TIMEOUT_MS,
  })
}

/** GET /api/freshness every 60 s while visible. */
export function useFreshnessOverview() {
  return useApiQuery<FreshnessOverview>(keys.freshness.overview(), fetchFreshness, {
    poll: FRESHNESS_POLL_MS, staleTime: 55_000, retry: 0,
  })
}

/** GET /api/ppc-freshness every 60 s while visible, every 4 s while a sync
 *  is running. Also the source of the PPC "data through" day. */
export function usePpcFreshness() {
  return useApiQuery<PpcFreshness>(keys.freshness.ppc(), fetchPpcFreshness, {
    poll: ppcCadence, staleTime: 55_000, retry: 0,
  })
}
